import {
  type EfsaSampnt,
  type OkProgram,
  type Purpose,
  type SubPurpose,
  type Substance,
  useCreateProductMutation,
  useCreateMatrixMutation,
  useCreateSubstanceMutation,
  useUpdateSubstanceMutation,
  useCreateOkProgramMutation,
  useUpdateOkProgramMutation,
  useCreatePurposeMutation,
  useUpdatePurposeMutation,
  useCreateSubPurposeMutation,
  useUpdateSubPurposeMutation,
  useCreateLabMutation,
  useCreateSampntMutation
} from 'prove-common'
import { ReferenceForm, type ReferenceField } from './ReferenceForm'
import { useUpdateSamplingSiteMutation } from './useUpdateSamplingSiteMutation'

const nameField: ReferenceField = { key: 'name', label: 'Navn', required: true }
const codeField: ReferenceField = { key: 'code', label: 'Kode', required: true }
const boundedName = { ...nameField, maxLength: 255 }
type CloseProps = { onClose: () => void }

export function ProductForm({ onClose }: CloseProps) {
  const create = useCreateProductMutation()
  return (
    <ReferenceForm
      title='Legg til produkt'
      fields={[nameField, codeField]}
      onClose={onClose}
      onSave={({ name, code }) => create.mutateAsync({ name, code })}
    />
  )
}

export function MatrixForm({ onClose }: CloseProps) {
  const create = useCreateMatrixMutation()
  return (
    <ReferenceForm
      title='Legg til matrise'
      fields={[nameField, codeField]}
      onClose={onClose}
      onSave={({ name, code }) => create.mutateAsync({ name, code })}
    />
  )
}

export function SubstanceForm({
  onClose,
  substance
}: CloseProps & { substance?: Substance }) {
  const create = useCreateSubstanceMutation()
  const update = useUpdateSubstanceMutation()
  return (
    <ReferenceForm
      title={substance ? 'Rediger substans' : 'Legg til substans'}
      fields={[
        nameField,
        codeField,
        {
          key: 'displayname',
          label: 'Visningsnavn',
          description: 'Valgfritt navn som vises til brukerne.'
        }
      ]}
      initialValues={
        substance
          ? {
              name: substance.name,
              code: substance.code,
              displayname: substance.displayname ?? ''
            }
          : undefined
      }
      onClose={onClose}
      onSave={({ name, code, displayname }) => {
        const body = { name, code, displayname: displayname || null }
        return substance
          ? update.mutateAsync({ id: substance.id, substance: body })
          : create.mutateAsync(body)
      }}
    />
  )
}

export function OkProgramForm({
  onClose,
  program
}: CloseProps & { program?: OkProgram }) {
  const create = useCreateOkProgramMutation()
  const update = useUpdateOkProgramMutation()
  return (
    <ReferenceForm
      title={program ? 'Rediger OK-program' : 'Legg til OK-program'}
      fields={[
        boundedName,
        { key: 'number', label: 'OK-nummer', required: true, maxLength: 50 }
      ]}
      initialValues={
        program ? { name: program.name, number: program.number } : undefined
      }
      onClose={onClose}
      onSave={({ name, number }) =>
        program
          ? update.mutateAsync({
              okProgramId: program.id,
              etag: program.etag,
              body: { name, number }
            })
          : create.mutateAsync({ name, number })
      }
    />
  )
}

export function PurposeForm({
  onClose,
  purpose
}: CloseProps & { purpose?: Purpose }) {
  const create = useCreatePurposeMutation()
  const update = useUpdatePurposeMutation()
  return (
    <ReferenceForm
      title={purpose ? 'Rediger hensikt' : 'Legg til hensikt'}
      fields={[boundedName]}
      initialValues={purpose ? { name: purpose.name } : undefined}
      onClose={onClose}
      onSave={({ name }) =>
        purpose
          ? update.mutateAsync({
              purposeId: purpose.id,
              etag: purpose.etag,
              body: { name }
            })
          : create.mutateAsync({ name })
      }
    />
  )
}

export function SubPurposeForm({
  onClose,
  subPurpose
}: CloseProps & { subPurpose?: SubPurpose }) {
  const create = useCreateSubPurposeMutation()
  const update = useUpdateSubPurposeMutation()
  return (
    <ReferenceForm
      title={subPurpose ? 'Rediger underhensikt' : 'Legg til underhensikt'}
      fields={[boundedName]}
      initialValues={subPurpose ? { name: subPurpose.name } : undefined}
      onClose={onClose}
      onSave={({ name }) =>
        subPurpose
          ? update.mutateAsync({
              subPurposeId: subPurpose.id,
              etag: subPurpose.etag,
              body: { name }
            })
          : create.mutateAsync({ name })
      }
    />
  )
}

export function SamplingSiteForm({
  onClose,
  site
}: CloseProps & { site?: EfsaSampnt }) {
  const create = useCreateSampntMutation()
  const update = useUpdateSamplingSiteMutation()
  return (
    <ReferenceForm
      title={site ? 'Rediger uttakssted' : 'Legg til uttakssted'}
      fields={[
        { key: 'nameNo', label: 'Navn (norsk)', required: true },
        { key: 'nameEn', label: 'Navn (engelsk)', required: true },
        codeField
      ]}
      initialValues={
        site
          ? { nameNo: site.nameNo, nameEn: site.nameEn, code: site.code }
          : undefined
      }
      onClose={onClose}
      onSave={({ nameNo, nameEn, code }) => {
        const body = { nameNo, nameEn, code }
        return site
          ? update.mutateAsync({ sampntId: site.id, body })
          : create.mutateAsync(body)
      }}
    />
  )
}

export function LabForm({ onClose }: CloseProps) {
  const create = useCreateLabMutation()
  return (
    <ReferenceForm
      title='Legg til laboratorium'
      fields={[
        nameField,
        { key: 'info', label: 'Informasjon', multiline: true }
      ]}
      onClose={onClose}
      onSave={({ name, info }) =>
        create.mutateAsync({
          name,
          info: info || undefined
        })
      }
    />
  )
}
